import type {
  RawData,
  TestDefinition,
  TestResult,
  ValidationContext,
  ValidationResult,
} from '../types.ts';
import { chromeArg, chromeArgs } from '../validation.ts';
import {
  errorText,
  formatError,
  numberValue,
  result,
  validation,
  validationError,
  workerValue,
} from './shared.ts';

async function collect(): Promise<RawData> {
  const dedicatedWorker = await workerValue(
    'self.navigator.hardwareConcurrency',
  );
  return {
    window: navigator.hardwareConcurrency,
    dedicatedWorker,
  };
}

function validateCpu(
  rawData: RawData,
  context: ValidationContext,
): ValidationResult {
  const reasons: string[] = [];
  const allowed = [2, 4, 6, 8, 12, 16, 20, 24, 32, 48, 64, 96, 128];
  const windowValue = numberValue(rawData, 'window');
  if (windowValue == null || !allowed.includes(windowValue)) {
    reasons.push(
      `${String(rawData.window)} is not an allowed Chrome CPU count`,
    );
  }
  if (rawData.dedicatedWorker !== windowValue) {
    reasons.push('Dedicated Worker CPU count does not match the window');
  }
  const args = chromeArgs(context);
  const configured = args ? chromeArg(args, 'cpu-count') : null;
  if (configured !== null && Number(configured) !== windowValue) {
    reasons.push(`CPU count does not match --cpu-count=${configured}`);
  }
  return validation(reasons);
}

function format(rawData: RawData): string {
  const error = formatError(rawData);
  if (error) {
    return error;
  }
  return `${String(rawData.window)} cores`;
}

async function run(): Promise<TestResult> {
  let rawData: RawData;
  try {
    rawData = await collect();
  } catch (error) {
    rawData = { error: errorText(error) };
  }
  return result(rawData, format);
}

function validate(
  rawData: RawData,
  context: ValidationContext,
): ValidationResult {
  const error = validationError(rawData);
  if (error) {
    return error;
  }
  return validateCpu(rawData, context);
}

export const cpuCountTest: TestDefinition = {
  id: 'cpuCount',
  title: 'CPU count',
  category: 'stableProfile',
  spoofingChromeArgs: ['cpu-count'],
  format,
  run,
  validate,
};
